import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal, PageSpinner } from '@/components/common';
import type { NotifLang } from '@/types/admin.types';
import { LangBar } from './LangBar';
import { OUPersonalizationSection } from './OUPersonalizationSection';
import type { OUOverride } from './OUPersonalizationSection';
import {
  useNotificationSettings,
  useSaveOmitirSettings,
  useSaveEuropaSettings,
  useSendGeneralNotification,
} from '@/hooks/useNotifications';
import { useUsers } from '@/hooks/useUsers';
import { useOUs, useGdprOuKey } from '@/hooks/useOUs';

const OMITIR_USER_MSGS: Record<NotifLang, string> = {
  es: `Hola {nombre},\n\nHemos notado que has omitido las últimas {count} encuestas de Pulse. Tu feedback es muy valioso para mejorar nuestra cultura de reuniones y ayudar a tus compañeros a crecer.\n\n¿Podrías tomarte 30 segundos para responder la próxima?\n\nGracias,\nEquipo de Cultura Sigma`,
  en: `Hi {nombre},\n\nWe've noticed you've skipped the last {count} Pulse surveys. Your feedback is very valuable to improve our meeting culture and help your teammates grow.\n\nCould you take 30 seconds to respond to the next one?\n\nThanks,\nSigma Culture Team`,
  pt: `Olá {nombre},\n\nNotamos que você não participou das últimas {count} pesquisas do Pulse. Sua opinião é muito valiosa para melhorar nossa cultura de reuniões.\n\nPoderia dedicar 30 segundos para responder a próxima?\n\nObrigado,\nEquipe de Cultura Sigma`,
  fr: `Bonjour {nombre},\n\nNous avons remarqué que vous avez omis les {count} dernières enquêtes Pulse. Votre feedback est très précieux pour améliorer notre culture de réunions.\n\nPourriez-vous prendre 30 secondes pour répondre à la prochaine?\n\nMerci,\nÉquipe Culture Sigma`,
  de: `Hallo {nombre},\n\nWir haben bemerkt, dass Sie die letzten {count} Pulse-Umfragen übersprungen haben. Ihr Feedback ist sehr wertvoll, um unsere Meetingkultur zu verbessern.\n\nKönnten Sie sich 30 Sekunden Zeit nehmen, um die nächste zu beantworten?\n\nDanke,\nSigma Kultur-Team`,
  nl: `Hallo {nombre},\n\nWe hebben gemerkt dat je de laatste {count} Pulse-enquêtes hebt overgeslagen. Jouw feedback is erg waardevol om onze vergadercultuur te verbeteren en je collega's te helpen groeien.\n\nZou je 30 seconden kunnen nemen om de volgende te beantwoorden?\n\nBedankt,\nSigma Cultuurteam`,
  ro: `Bună {nombre},\n\nAm observat că ai omis ultimele {count} sondaje Pulse. Feedback-ul tău este foarte valoros pentru a îmbunătăți cultura întâlnirilor noastre și a ajuta colegii tăi să crească.\n\nAi putea acorda 30 de secunde pentru a răspunde la următorul?\n\nMulțumim,\nEchipa de Cultură Sigma`,
};

const OMITIR_MGR_MSGS: Record<NotifLang, string> = {
  es: `Hola {manager},\n\n{nombre} de tu equipo ha omitido {count} encuestas consecutivas de Pulse. Te sugerimos conversar con él/ella para entender si hay algún bloqueo.\n\nEquipo de Cultura Sigma`,
  en: `Hi {manager},\n\n{nombre} from your team has skipped {count} consecutive Pulse surveys. We suggest having a conversation to understand if there's any blocker.\n\nSigma Culture Team`,
  pt: `Olá {manager},\n\n{nombre} da sua equipe omitiu {count} pesquisas consecutivas do Pulse. Sugerimos conversar para entender se há algum bloqueio.\n\nEquipe de Cultura Sigma`,
  fr: `Bonjour {manager},\n\n{nombre} de votre équipe a omis {count} enquêtes Pulse consécutives. Nous vous suggérons d'en discuter pour comprendre s'il y a un blocage.\n\nÉquipe Culture Sigma`,
  de: `Hallo {manager},\n\n{nombre} aus Ihrem Team hat {count} aufeinanderfolgende Pulse-Umfragen übersprungen. Wir empfehlen ein Gespräch, um mögliche Hindernisse zu verstehen.\n\nSigma Kultur-Team`,
  nl: `Hallo {manager},\n\n{nombre} van je team heeft {count} opeenvolgende Pulse-enquêtes overgeslagen. We raden aan een gesprek te voeren om te begrijpen of er een obstakel is.\n\nSigma Cultuurteam`,
  ro: `Bună {manager},\n\n{nombre} din echipa ta a omis {count} sondaje Pulse consecutive. Îți sugerăm să discuți cu el/ea pentru a înțelege dacă există vreun blocaj.\n\nEchipa de Cultură Sigma`,
};

const EUROPA_USER_MSGS: Record<NotifLang, string> = {
  es: `Estimado/a {nombre},\n\nHemos observado que no has participado recientemente en las encuestas de reunión de tu equipo. Tu opinión es importante para nosotros.\n\n¿Podrías compartir tu retroalimentación en la próxima oportunidad?\n\nGracias,\n[Nombre y firma — personalizar antes de enviar]`,
  en: `Dear {nombre},\n\nWe have noticed that you have not recently participated in your team's meeting surveys. Your opinion is important to us.\n\nCould you share your feedback at the next opportunity?\n\nThank you,\n[Name and signature — customize before sending]`,
  pt: `Prezado/a {nombre},\n\nObservamos que você não participou recentemente das pesquisas de reunião da sua equipe. Sua opinião é importante para nós.\n\nPoderia compartilhar seu feedback na próxima oportunidade?\n\nObrigado,\n[Nome e assinatura — personalizar antes de enviar]`,
  fr: `Cher/Chère {nombre},\n\nNous avons observé que vous n'avez pas récemment participé aux enquêtes de réunion de votre équipe. Votre avis est important pour nous.\n\nPourriez-vous partager vos commentaires à la prochaine occasion?\n\nMerci,\n[Nom et signature — à personnaliser avant l'envoi]`,
  de: `Sehr geehrte/r {nombre},\n\nWir haben festgestellt, dass Sie in letzter Zeit nicht an den Teambesprechungs-Umfragen teilgenommen haben. Ihre Meinung ist uns wichtig.\n\nKönnten Sie bei der nächsten Gelegenheit Ihr Feedback teilen?\n\nMit freundlichen Grüßen,\n[Name und Unterschrift — vor dem Senden personalisieren]`,
  nl: `Beste {nombre},\n\nWe hebben gemerkt dat je recent niet hebt deelgenomen aan de vergaderenquêtes van je team. Jouw mening is belangrijk voor ons.\n\nZou je bij de volgende gelegenheid je feedback kunnen delen?\n\nBedankt,\n[Naam en handtekening — personaliseren vóór verzending]`,
  ro: `Stimate/Stimată {nombre},\n\nAm observat că nu ai participat recent la sondajele de întâlnire ale echipei tale. Opinia ta este importantă pentru noi.\n\nAi putea împărtăși feedback-ul tău la următoarea ocazie?\n\nMulțumim,\n[Nume și semnătură — personalizați înainte de trimitere]`,
};

const EUROPA_MGR_MSGS: Record<NotifLang, string> = {
  es: `Estimado/a {manager},\n\n{nombre} de tu equipo no ha participado en las últimas encuestas de reunión. Te sugerimos tener una conversación directa en lugar de un recordatorio automático, en línea con las políticas GDPR de Europa.\n\n[Adaptar y revisar antes de enviar]`,
  en: `Dear {manager},\n\n{nombre} from your team has not participated in recent meeting surveys. We suggest having a direct conversation rather than an automated reminder, in line with Europa's GDPR policies.\n\n[Adapt and review before sending]`,
  pt: `Prezado/a {manager},\n\n{nombre} da sua equipe não participou das pesquisas de reunião recentes. Sugerimos uma conversa direta em vez de um lembrete automático, em conformidade com as políticas GDPR da Europa.\n\n[Adaptar e revisar antes de enviar]`,
  fr: `Cher/Chère {manager},\n\n{nombre} de votre équipe n'a pas participé aux enquêtes de réunion récentes. Nous vous suggérons d'avoir une conversation directe plutôt qu'un rappel automatique, conformément aux politiques RGPD de l'Europe.\n\n[Adapter et réviser avant l'envoi]`,
  de: `Sehr geehrte/r {manager},\n\n{nombre} aus Ihrem Team hat nicht an den letzten Teambesprechungs-Umfragen teilgenommen. Wir empfehlen ein direktes Gespräch anstelle einer automatischen Erinnerung, gemäß der DSGVO-Richtlinien für Europa.\n\n[Vor dem Senden anpassen und überprüfen]`,
  nl: `Beste {manager},\n\n{nombre} van je team heeft niet deelgenomen aan de recente vergaderenquêtes. We raden aan een direct gesprek te voeren in plaats van een automatische herinnering, in overeenstemming met het AVG-beleid van Europa.\n\n[Aanpassen en beoordelen vóór verzending]`,
  ro: `Stimate/Stimată {manager},\n\n{nombre} din echipa ta nu a participat la sondajele recente de întâlnire. Îți sugerăm să ai o conversație directă în loc de un memento automat, conform politicilor GDPR ale Europei.\n\n[Adaptați și revizuiți înainte de trimitere]`,
};

export function NotificationsTab() {
  const { t } = useTranslation();

  // OMITIR
  const [omitirEnabled, setOmitirEnabled] = useState(true);
  const [omitirCount, setOmitirCount] = useState(3);
  const [omitirNotifyUser, setOmitirNotifyUser] = useState(true);
  const [omitirNotifyManager, setOmitirNotifyManager] = useState(true);
  const [omitirNotifyAdminOU, setOmitirNotifyAdminOU] = useState(false);
  const [omitirUserLang, setOmitirUserLang] = useState<NotifLang>('es');
  const [omitirMgrLang, setOmitirMgrLang] = useState<NotifLang>('es');
  const [omitirUserMsg, setOmitirUserMsg] = useState<Record<NotifLang, string>>({ ...OMITIR_USER_MSGS });
  const [omitirMgrMsg, setOmitirMgrMsg] = useState<Record<NotifLang, string>>({ ...OMITIR_MGR_MSGS });
  const [omitirOUs, setOmitirOUs] = useState<OUOverride[]>([
    { id: '1', ou: 'latam', lang: 'es', userMsg: 'Hola {nombre},\n\nHemos notado que no has participado en las últimas {count} encuestas. Tu opinión es muy valiosa para nuestro equipo en LATAM.\n\nGracias,\nEquipo de Cultura Sigma', mgrMsg: '{nombre} de tu equipo (LATAM) ha omitido {count} encuestas. Te sugerimos tener una conversación de seguimiento.' },
  ]);

  // Europa
  const [europaEnabled, setEuropaEnabled] = useState(false);
  const [europaConfirm, setEuropaConfirm] = useState(false);
  const [europaUserLang, setEuropaUserLang] = useState<NotifLang>('fr');
  const [europaMgrLang, setEuropaMgrLang] = useState<NotifLang>('fr');
  const [europaUserMsg, setEuropaUserMsg] = useState<Record<NotifLang, string>>({ ...EUROPA_USER_MSGS });
  const [europaMgrMsg, setEuropaMgrMsg] = useState<Record<NotifLang, string>>({ ...EUROPA_MGR_MSGS });

  // General
  const [generalMsg, setGeneralMsg] = useState('');
  const [generalAudience, setGeneralAudience] = useState('global');
  const [includeEuropa, setIncludeEuropa] = useState(false);
  const [generalOUs, setGeneralOUs] = useState<OUOverride[]>([
    { id: '1', ou: 'usa', lang: 'en', userMsg: '', mgrMsg: '' },
  ]);

  function addOmitirOU() {
    setOmitirOUs(ov => [...ov, { id: String(Date.now()), ou: 'mexico', lang: 'es', userMsg: '', mgrMsg: '' }]);
  }
  function removeOmitirOU(id: string) { setOmitirOUs(ov => ov.filter(o => o.id !== id)); }
  function updateOmitirOU(id: string, field: keyof OUOverride, value: string) {
    setOmitirOUs(ov => ov.map(o => o.id === id ? { ...o, [field]: value } : o));
  }

  function addGeneralOU() {
    setGeneralOUs(ov => [...ov, { id: String(Date.now()), ou: 'mexico', lang: 'es', userMsg: '', mgrMsg: '' }]);
  }
  function removeGeneralOU(id: string) { setGeneralOUs(ov => ov.filter(o => o.id !== id)); }
  function updateGeneralOU(id: string, field: keyof OUOverride, value: string) {
    setGeneralOUs(ov => ov.map(o => o.id === id ? { ...o, [field]: value } : o));
  }

  const { data: loadedSettings, isLoading: loadingSettings } = useNotificationSettings();
  const saveOmitir  = useSaveOmitirSettings();
  const saveEuropa  = useSaveEuropaSettings();
  const sendGeneral = useSendGeneralNotification();
  const { data: usersData } = useUsers();
  const { data: allOUs = [] } = useOUs();
  const mainOUs = allOUs.filter(o => !o.gdpr);
  const gdprOuKey = useGdprOuKey();

  // Populate state from backend settings on first load
  useEffect(() => {
    if (!loadedSettings) return;
    setOmitirEnabled(loadedSettings.omitirEnabled);
    setOmitirCount(loadedSettings.omitirThreshold);
    setOmitirNotifyUser(loadedSettings.omitirNotifyUser ?? true);
    setOmitirNotifyManager(loadedSettings.omitirNotifyManager ?? true);
    setOmitirNotifyAdminOU(loadedSettings.omitirNotifyAdminOU ?? false);
    if (loadedSettings.omitirUserTemplates && Object.keys(loadedSettings.omitirUserTemplates).length > 0) {
      setOmitirUserMsg(prev => ({ ...prev, ...loadedSettings.omitirUserTemplates as Record<string, string> }));
    }
    if (loadedSettings.omitirMgrTemplates && Object.keys(loadedSettings.omitirMgrTemplates).length > 0) {
      setOmitirMgrMsg(prev => ({ ...prev, ...loadedSettings.omitirMgrTemplates as Record<string, string> }));
    }
    setEuropaEnabled(loadedSettings.europaEnabled);
    if (loadedSettings.europaUserTemplates && Object.keys(loadedSettings.europaUserTemplates).length > 0) {
      setEuropaUserMsg(prev => ({ ...prev, ...loadedSettings.europaUserTemplates as Record<string, string> }));
    }
    if (loadedSettings.europaMgrTemplates && Object.keys(loadedSettings.europaMgrTemplates).length > 0) {
      setEuropaMgrMsg(prev => ({ ...prev, ...loadedSettings.europaMgrTemplates as Record<string, string> }));
    }
    if (loadedSettings.omitirOuOverrides?.length) {
      setOmitirOUs(loadedSettings.omitirOuOverrides.map((o, i) => ({
        id: String(i), ou: o.ouId, lang: (o.language as import('@/types/admin.types').NotifLang) ?? 'es',
        userMsg: o.userMessage ?? '', mgrMsg: o.managerMessage ?? '',
      })));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedSettings]);

  function handleSaveOmitir() {
    saveOmitir.mutate({
      omitirEnabled:      omitirEnabled,
      omitirThreshold:    omitirCount,
      omitirNotifyUser:   omitirNotifyUser,
      omitirNotifyManager: omitirNotifyManager,
      omitirNotifyAdminOU:  omitirNotifyAdminOU,
      omitirUserTemplates:  omitirUserMsg as Record<string, string>,
      omitirMgrTemplates:   omitirMgrMsg as Record<string, string>,
      omitirOuOverrides:    omitirOUs.map(o => ({
        ouId: o.ou, language: o.lang, userMessage: o.userMsg, managerMessage: o.mgrMsg,
      })),
    });
  }

  function handleSaveEuropa() {
    saveEuropa.mutate({
      europaEnabled:       europaEnabled,
      europaUserTemplates: europaUserMsg as Record<string, string>,
      europaMgrTemplates:  europaMgrMsg as Record<string, string>,
    });
  }

  function handleSendGeneral() {
    const allUsers = usersData ?? [];
    const filtered = generalAudience === 'global'
      ? allUsers.filter(u => includeEuropa || u.ou !== gdprOuKey)
      : allUsers.filter(u => u.ou === generalAudience);
    const recipientEmails = filtered.map(u => u.email).filter((e): e is string => Boolean(e));

    sendGeneral.mutate({
      subject: t('admin.generalNotificationsTitle'),
      body: generalMsg,
      recipientEmails,
      audienceOuId: generalAudience,
    });
  }

  function handleEuropaToggle(checked: boolean) {
    if (checked) {
      setEuropaConfirm(true);
    } else {
      setEuropaEnabled(false);
    }
  }

  function confirmEuropaActivate() {
    setEuropaEnabled(true);
    setEuropaConfirm(false);
  }

  return (
    <div className="adm-tab-content">
      {europaConfirm && (
        <ConfirmModal
          type="warning"
          title={t('admin.europaActivateTitle')}
          message={t('admin.europaActivateDesc')}
          confirmText={t('admin.europaActivateConfirm')}
          onConfirm={confirmEuropaActivate}
          onClose={() => setEuropaConfirm(false)}
        />
      )}

      <header className="page-header page-header--center">
        <h2 className="page-title">{t('admin.notificationsTitle')}</h2>
      </header>

      {loadingSettings && <PageSpinner />}
      <div className="notifications-grid" style={{ display: loadingSettings ? 'none' : undefined }}>

        {/* OMITIR */}
        <div className="notification-card">
          <div className="notification-header">
            <h3><i className="fas fa-forward" /> {t('admin.notifOmitirTitle')}</h3>
            <label className="switch">
              <input type="checkbox" checked={omitirEnabled} onChange={e => setOmitirEnabled(e.target.checked)} />
              <span className="slider" />
            </label>
          </div>
          <div className="notification-body" style={{ opacity: omitirEnabled ? 1 : 0.5, pointerEvents: omitirEnabled ? 'auto' : 'none' }}>
            <div className="setting-item setting-item--inline">
              <label>{t('admin.notifyAfter')}</label>
              <div className="input-group">
                <input type="number" className="input-small" value={omitirCount} min={1} max={10}
                  onChange={e => setOmitirCount(Number(e.target.value))} />
                <span className="input-suffix">{t('admin.consecutiveOmissions')}</span>
              </div>
            </div>
            <div className="setting-item">
              <label>{t('admin.notifyTo')}</label>
              <div className="notif-recipient-list">
                <label className="notif-recipient-item">
                  <input type="checkbox" checked={omitirNotifyUser} onChange={e => setOmitirNotifyUser(e.target.checked)} />
                  <span className="notif-recipient-check"><i className="fas fa-check" /></span>
                  <span className="notif-recipient-label">{t('admin.notifyUser')}</span>
                </label>
                <label className="notif-recipient-item">
                  <input type="checkbox" checked={omitirNotifyManager} onChange={e => setOmitirNotifyManager(e.target.checked)} />
                  <span className="notif-recipient-check"><i className="fas fa-check" /></span>
                  <span className="notif-recipient-label">{t('admin.notifyDirectManager')}</span>
                </label>
                <label className="notif-recipient-item">
                  <input type="checkbox" checked={omitirNotifyAdminOU} onChange={e => setOmitirNotifyAdminOU(e.target.checked)} />
                  <span className="notif-recipient-check"><i className="fas fa-check" /></span>
                  <span className="notif-recipient-label">{t('admin.notifyAdminOU')}</span>
                </label>
              </div>
            </div>

            {/* User message */}
            <div className="setting-item full-width">
              <label>{t('admin.messageForUser')}</label>
              <LangBar active={omitirUserLang} onSwitch={setOmitirUserLang} group="omitir-user" />
              <textarea className="message-textarea" rows={4}
                value={omitirUserMsg[omitirUserLang]}
                onChange={e => setOmitirUserMsg(prev => ({ ...prev, [omitirUserLang]: e.target.value }))}
              />
              <div className="variables-hint">{t('admin.availableVarsOmitir')}</div>
            </div>

            {/* Manager message */}
            <div className="setting-item full-width">
              <label>{t('admin.messageForManager')}</label>
              <LangBar active={omitirMgrLang} onSwitch={setOmitirMgrLang} group="omitir-mgr" />
              <textarea className="message-textarea" rows={3}
                value={omitirMgrMsg[omitirMgrLang]}
                onChange={e => setOmitirMgrMsg(prev => ({ ...prev, [omitirMgrLang]: e.target.value }))}
              />
            </div>
          </div>

          <div style={{ opacity: omitirEnabled ? 1 : 0.5, pointerEvents: omitirEnabled ? 'auto' : 'none' }}>
            <OUPersonalizationSection
              overrides={omitirOUs}
              onAdd={addOmitirOU}
              onRemove={removeOmitirOU}
              onUpdate={updateOmitirOU}
              showMgrField={true}
            />
          </div>

          <div className="rules-card-footer">
            <button className="rules-save-btn" onClick={handleSaveOmitir} disabled={saveOmitir.isPending}>
              <i className="fas fa-save" /> {saveOmitir.isPending ? '...' : t('admin.saveChanges')}
            </button>
          </div>
        </div>

        {/* Europa GDPR */}
        <div className="notification-card europa-notif-card">
          <div className="notification-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3><i className="fas fa-shield-alt" /> {t('admin.europaOmissionsTitle')}</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="europa-notif-badge"><i className="fas fa-lock" /> {t('admin.manualOnlyGDPR')}</span>
              <label className="switch">
                <input type="checkbox" checked={europaEnabled}
                  onChange={e => handleEuropaToggle(e.target.checked)} />
                <span className="slider" />
              </label>
            </div>
          </div>
          <div className="notification-body" style={{ opacity: europaEnabled ? 1 : 0.4, pointerEvents: europaEnabled ? 'auto' : 'none' }}>
            {!europaEnabled && (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--gray-400)', fontSize: 13, fontWeight: 600 }}>
                <i className="fas fa-power-off" style={{ fontSize: 20, display: 'block', marginBottom: 8, color: 'var(--gray-300)' }} />
                {t('admin.europaSectionDisabled')}
              </div>
            )}
            <div className="europa-notif-banner">
              <i className="fas fa-balance-scale" />
              <p>{t('admin.gdprWarning')}</p>
            </div>

            {/* Employee template with lang switcher */}
            <div className="setting-item full-width">
              <label>{t('admin.emailTemplateEmployee')}</label>
              <LangBar active={europaUserLang} onSwitch={setEuropaUserLang} group="europa-user" />
              <textarea className="message-textarea" rows={6}
                value={europaUserMsg[europaUserLang]}
                onChange={e => setEuropaUserMsg(prev => ({ ...prev, [europaUserLang]: e.target.value }))}
              />
              <div className="variables-hint">
                Variables: {'{nombre}'}, {'{fecha}'}. <strong>{t('admin.reviewBeforeSending')}</strong>
              </div>
            </div>

            {/* Manager template with lang switcher */}
            <div className="setting-item full-width">
              <label>{t('admin.emailTemplateManager')}</label>
              <LangBar active={europaMgrLang} onSwitch={setEuropaMgrLang} group="europa-mgr" />
              <textarea className="message-textarea" rows={4}
                value={europaMgrMsg[europaMgrLang]}
                onChange={e => setEuropaMgrMsg(prev => ({ ...prev, [europaMgrLang]: e.target.value }))}
              />
            </div>

            <div className="europa-notif-warning">
              <i className="fas fa-exclamation-triangle" />
              <span>
                {t('admin.europaNoAutoSend')}
              </span>
            </div>
          </div>
          <div className="rules-card-footer">
            <button className="rules-save-btn" onClick={handleSaveEuropa} disabled={saveEuropa.isPending}>
              <i className="fas fa-save" /> {saveEuropa.isPending ? '...' : t('admin.saveTemplates')}
            </button>
          </div>
        </div>

        {/* General Notifications — full width */}
        <div className="notification-card notif-card-full">
          <div className="notification-header">
            <h3><i className="fas fa-bullhorn" /> {t('admin.generalNotificationsTitle')}</h3>
          </div>
          <div className="notification-body">
            <div className="setting-item full-width">
              <label>{t('admin.notifMessageLabel')}</label>
              <textarea className="message-textarea" rows={4} placeholder={t('admin.notifMessagePlaceholder')}
                value={generalMsg} onChange={e => setGeneralMsg(e.target.value)} />
              <div className="variables-hint">{t('admin.availableVarsGeneral')}</div>
            </div>
            <div className="setting-item full-width">
              <label>{t('admin.notifAudienceLabel')}</label>
              <select className="form-input" style={{ maxWidth: 300 }} value={generalAudience}
                onChange={e => setGeneralAudience(e.target.value)}>
                <option value="global">{t('admin.notifAudienceGlobal')}</option>
                {mainOUs.map(ou => (
                  <option key={ou.key} value={ou.key}>{ou.label}</option>
                ))}
              </select>
            </div>
            <div style={{ marginTop: 12, padding: '12px 16px', background: 'rgba(220,38,38,0.04)', border: '1px solid rgba(220,38,38,0.12)', borderRadius: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                <input type="checkbox" style={{ width: 16, height: 16 }}
                  checked={includeEuropa} onChange={e => setIncludeEuropa(e.target.checked)} />
                <span>
                  <strong style={{ color: 'var(--gray-900)' }}>{t('admin.includeEuropa')}</strong>
                  {' — '}{t('admin.includeEuropaHint')}
                </span>
              </label>
            </div>
          </div>

          <OUPersonalizationSection
            overrides={generalOUs}
            onAdd={addGeneralOU}
            onRemove={removeGeneralOU}
            onUpdate={updateGeneralOU}
            showMgrField={false}
          />

          <div className="notification-body" style={{ paddingTop: 0, borderTop: '1px solid rgba(52,47,130,0.06)' }}>
            <div style={{ padding: '12px 0 0' }}>
              <button className="btn btn--primary" onClick={handleSendGeneral} disabled={sendGeneral.isPending || !generalMsg.trim()}>
                <i className="fas fa-paper-plane" /> {sendGeneral.isPending ? '...' : t('admin.sendNotification')}
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
